import { MilestoneStatus } from '@thoughtspot/blink-context';
import {
    PropertyMap,
    UserAction,
    userActionTypeEnum,
} from '@thoughtspot/metrics';

export type ChecklistCollectEvent = (
    userAction: UserAction,
    property: PropertyMap,
) => void;

export interface ChecklistBaseProperties extends PropertyMap {
    selectedFunction: string;
    selectedIndustry: string;
    sectionOpened: string;
    taskOpened: string;
}

export interface ChecklistOpenedProperties extends ChecklistBaseProperties {
    loadType: LoadType;
}

export interface ChecklistClosedProperties extends ChecklistBaseProperties {
    sectionSelected: string;
    taskSelected: string;
    loadType: LoadType;
}

export interface ChecklistSectionSelectedProperties
    extends ChecklistBaseProperties {
    sectionSelected: string;
}

export interface ChecklistTaskSelectedProperties
    extends ChecklistBaseProperties {
    sectionSelected: string;
    taskSelected: string;
    loadType: LoadType;
}

export interface ChecklistErrorProperties extends ChecklistClosedProperties {
    errorType: string;
}

export const checklistOpened = (
    collectEvent: ChecklistCollectEvent,
    properties: ChecklistOpenedProperties,
) => {
    collectEvent(
        new UserAction(userActionTypeEnum.CHECKLIST_OPENED),
        properties,
    );
};

export const checklistClosed = (
    collectEvent: ChecklistCollectEvent,
    properties: ChecklistClosedProperties,
) => {
    collectEvent(
        new UserAction(userActionTypeEnum.CHECKLIST_CLOSED),
        properties,
    );
};

export const checklistSectionSelected = (
    collectEvent: ChecklistCollectEvent,
    properties: ChecklistSectionSelectedProperties,
) => {
    collectEvent(
        new UserAction(userActionTypeEnum.CHECKLIST_SECTION_SELECTED),
        properties,
    );
};

export interface ChecklistSectionCompletedProperties
    extends ChecklistBaseProperties {
    agentCardSelected: string;
    sectionSelected: string;
    taskSelected: string;
    loadType: LoadType;
}

export const checklistSectionCompleted = (
    collectEvent: ChecklistCollectEvent,
    properties: ChecklistSectionCompletedProperties,
) => {
    collectEvent(
        new UserAction(userActionTypeEnum.CHECKLIST_SECTION_COMPLETED),
        properties,
    );
};

export const checklistTaskSelected = (
    collectEvent: ChecklistCollectEvent,
    properties: ChecklistTaskSelectedProperties,
) => {
    collectEvent(
        new UserAction(userActionTypeEnum.CHECKLIST_TASK_SELECTED),
        properties,
    );
};

export const checklistTaskCtaClicked = (
    collectEvent: ChecklistCollectEvent,
    properties: ChecklistTaskSelectedProperties,
) => {
    collectEvent(
        new UserAction(userActionTypeEnum.CHECKLIST_TASK_CTA_CLICKED),
        properties,
    );
};

export const checklistTaskCompleted = (
    collectEvent: ChecklistCollectEvent,
    properties: ChecklistSectionCompletedProperties,
) => {
    collectEvent(
        new UserAction(userActionTypeEnum.CHECKLIST_TASK_COMPLETED),
        properties,
    );
};

export const checklistErrorShown = (
    collectEvent: ChecklistCollectEvent,
    properties: ChecklistErrorProperties,
) => {
    collectEvent(
        new UserAction(userActionTypeEnum.CHECKLIST_ERROR_SHOWN),
        properties,
    );
};

export enum LoadType {
    MANUAL = 'manual',
    AUTO = 'auto',
}

export interface ChecklistGuideProperties extends PropertyMap {
    selectedFunction: string;
    selectedIndustry: string;
    sectionOpened: string;
    sectionSelected: string;
    taskOpened: string;
    taskSelected: string;
    loadType: LoadType;
}

export const checklistGuideLoaded = (
    collectEvent: ChecklistCollectEvent,
    properties: ChecklistGuideProperties,
) => {
    collectEvent(
        new UserAction(userActionTypeEnum.CHECKLIST_GUIDE_LOADED),
        properties,
    );
};

export const checklistGuideCtaClicked = (
    collectEvent: ChecklistCollectEvent,
    properties: ChecklistGuideProperties,
) => {
    collectEvent(
        new UserAction(userActionTypeEnum.CHECKLIST_GUIDE_CTA_CLICKED),
        properties,
    );
};

export const checklistGuideClosed = (
    collectEvent: ChecklistCollectEvent,
    properties: ChecklistGuideProperties,
) => {
    collectEvent(
        new UserAction(userActionTypeEnum.CHECKLIST_GUIDE_CLOSED),
        properties,
    );
};

export interface MilestoneModalProperties extends PropertyMap {
    selectedFunction: string;
    selectedIndustry: string;
    agentCardSelected: string;
    milestoneReached: MilestoneStatus;
}

export const milestoneModalLoaded = (
    collectEvent: ChecklistCollectEvent,
    properties: MilestoneModalProperties,
) => {
    collectEvent(
        new UserAction(userActionTypeEnum.MILESTONE_MODAL_LOADED),
        properties,
    );
};

export const milestoneModalClosed = (
    collectEvent: ChecklistCollectEvent,
    properties: MilestoneModalProperties,
) => {
    collectEvent(
        new UserAction(userActionTypeEnum.MILESTONE_MODAL_CLOSED),
        properties,
    );
};

export interface MilestoneCtaClickedProperties
    extends MilestoneModalProperties {
    ctaClicked: string;
}

export const milestoneCtaClicked = (
    collectEvent: ChecklistCollectEvent,
    properties: MilestoneCtaClickedProperties,
) => {
    collectEvent(
        new UserAction(userActionTypeEnum.MILESTONE_CTA_CLICKED),
        properties,
    );
};

export enum FooterCtaType {
    INVITE = 'invite',
    CONNECT_DATA = 'connect data',
}

export interface ChecklistFooterCtaClickedProperties extends PropertyMap {
    selectedFunction: string;
    selectedIndustry: string;
    agentCardSelected: string;
    ctaClicked: FooterCtaType;
}

export const checklistFooterCtaClicked = (
    collectEvent: ChecklistCollectEvent,
    properties: ChecklistFooterCtaClickedProperties,
) => {
    collectEvent(
        new UserAction(userActionTypeEnum.CHECKLIST_FOOTER_CTA_CLICKED),
        properties,
    );
};
